import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetSupplierList,
  GetSupplierDetail,
  GetSupplierListForPayment,
  GetSupplierListForFieldSelect,
  CreateSupplier,
  UpdateSuplier,
  DeleteSupplier,
} from "../../Services/SupplierService";

/**
 * Supplier State Interface with caching and optimization
 */
export interface SupplierState {
  // List data
  supplierCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;

  // Detail data
  selectedSupplierDetail: any | null;

  // Payment mode suppliers
  paymentSuppliers: any[];

  // Field select mode suppliers
  fieldSelectSuppliers: any[];

  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;

  // Error states
  error: string | null;
  errorDetail: string | null;

  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  mode: string;

  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

/**
 * Async thunk for fetching supplier list
 */
export const fetchSupplierList = createAsyncThunk(
  "supplier/fetchList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
      mode: string;
      financialYear: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        queryParams.append(key, String(value));
      });

      const result: any = await GetSupplierList(queryParams);
      if (result?.data?.data) {
        return result.data.data;
      }
      throw new Error(result?.data?.message || "Failed to fetch suppliers");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch suppliers"
      );
    }
  }
);

/**
 * Async thunk for fetching supplier detail
 */
export const fetchSupplierDetail = createAsyncThunk(
  "supplier/fetchDetail",
  async (
    params: {
      consumerId: number;
      supplierId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        queryParams.append(key, String(value));
      });

      const result: any = await GetSupplierDetail(queryParams);
      if (result?.data?.data) {
        return result.data.data;
      }
      throw new Error(result?.data?.message || "Failed to fetch supplier");
    } catch (error: any) {
      return rejectWithValue(error?.Message || error?.message || error || "Failed to fetch supplier details");
    }
  }
);

/**
 * Async thunk for fetching suppliers for payment
 */
export const fetchSuppliersForPayment = createAsyncThunk(
  "supplier/fetchForPayment",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        queryParams.append(key, String(value));
      });

      const result: any = await GetSupplierListForPayment(queryParams);
      if (result?.data?.data) {
        return result.data.data;
      }
      throw new Error(result?.data?.message || "Failed to fetch suppliers");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch suppliers for payment"
      );
    }
  }
);

/**
 * Async thunk for fetching suppliers for field select
 */
export const fetchSuppliersForFieldSelect = createAsyncThunk(
  "supplier/fetchForFieldSelect",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        queryParams.append(key, String(value));
      });

      const result: any = await GetSupplierListForFieldSelect(queryParams);
      if (result?.data?.data) {
        return result.data.data;
      }
      throw new Error(result?.data?.message || "Failed to fetch suppliers");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch field select suppliers"
      );
    }
  }
);

/**
 * Async thunk for creating supplier
 */
export const createSupplierAsync = createAsyncThunk(
  "supplier/create",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateSupplier(data);
      if (result?.data?.data) {
        return result.data.data;
      }
      throw new Error(result?.data?.message || "Failed to create supplier");
    } catch (error: any) {
      return rejectWithValue(error?.Message || error?.message || error || "Failed to create supplier");
    }
  }
);

/**
 * Async thunk for updating supplier
 */
export const updateSupplierAsync = createAsyncThunk(
  "supplier/update",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateSuplier(data);
      if (result?.data?.data) {
        return result.data.data;
      }
      throw new Error(result?.data?.message || "Failed to update supplier");
    } catch (error: any) {
      return rejectWithValue(error?.Message || error?.message || error || "Failed to update supplier");
    }
  }
);

/**
 * Async thunk for deleting supplier
 */
export const deleteSupplierAsync = createAsyncThunk(
  "supplier/delete",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteSupplier(data);
      if (result?.data) {
        return result.data;
      }
      throw new Error(result?.data?.message || "Failed to delete supplier");
    } catch (error: any) {
      return rejectWithValue(error?.Message || error?.message || error || "Failed to delete supplier");
    }
  }
);

// Initial state
const initialState: SupplierState = {
  supplierCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedSupplierDetail: null,
  paymentSuppliers: [],
  fieldSelectSuppliers: [],
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  mode: "",
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

/**
 * Supplier Slice
 */
const supplierSlice = createSlice({
  name: "supplier",
  initialState,
  reducers: {
    // Synchronous actions
    setSupplierMetadata: (
      state,
      action: PayloadAction<{
        pageIndex?: number;
        searchValue?: string;
        sortColumn?: string;
        sortDirection?: string;
        mode?: string;
      }>
    ) => {
      if (action.payload.pageIndex !== undefined) {
        state.pageIndex = action.payload.pageIndex;
      }
      if (action.payload.searchValue !== undefined) {
        state.searchValue = action.payload.searchValue;
      }
      if (action.payload.sortColumn !== undefined) {
        state.sortColumn = action.payload.sortColumn;
      }
      if (action.payload.sortDirection !== undefined) {
        state.sortDirection = action.payload.sortDirection;
      }
      if (action.payload.mode !== undefined) {
        state.mode = action.payload.mode;
      }
    },
    clearSupplierError: (state) => {
      state.error = null;
      state.errorDetail = null;
    },
    clearSupplierSuccess: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
    resetSupplierState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch supplier list
    builder
      .addCase(fetchSupplierList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchSupplierList.fulfilled, (state, action) => {
        state.loadingList = false;
        state.supplierCollection = action.payload.supplierCollection || [];
        state.totalCount = action.payload.totalCount || 0;
      })
      .addCase(fetchSupplierList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    // Fetch supplier detail
    builder
      .addCase(fetchSupplierDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(fetchSupplierDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.selectedSupplierDetail = action.payload;
      })
      .addCase(fetchSupplierDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    // Fetch suppliers for payment
    builder
      .addCase(fetchSuppliersForPayment.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(fetchSuppliersForPayment.fulfilled, (state, action) => {
        state.loadingList = false;
        state.paymentSuppliers = action.payload || [];
      })
      .addCase(fetchSuppliersForPayment.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    // Fetch suppliers for field select
    builder
      .addCase(fetchSuppliersForFieldSelect.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(fetchSuppliersForFieldSelect.fulfilled, (state, action) => {
        state.loadingList = false;
        state.fieldSelectSuppliers = action.payload || [];
      })
      .addCase(fetchSuppliersForFieldSelect.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    // Create supplier
    builder
      .addCase(createSupplierAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
      })
      .addCase(createSupplierAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
      })
      .addCase(createSupplierAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    // Update supplier
    builder
      .addCase(updateSupplierAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
      })
      .addCase(updateSupplierAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
      })
      .addCase(updateSupplierAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    // Delete supplier
    builder
      .addCase(deleteSupplierAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
      })
      .addCase(deleteSupplierAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
      })
      .addCase(deleteSupplierAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSupplierMetadata,
  clearSupplierError,
  clearSupplierSuccess,
  resetSupplierState,
} = supplierSlice.actions;

export default supplierSlice.reducer;
